import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { audioManager } from 'lib/audio/audio-manager';
import { AUDIO_TYPES } from 'lib/audio/audio.types';

interface IPrimaryButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text?: string;
  svg?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  innerClassName?: string;
  isAnimated?: boolean;
  buttonType?: 'primary' | 'return' | 'accept';
  isMobile?: boolean;
  onEnd?: () => void;
}

const buttonOptions = {
  primary: {
    buttonStyles: 'bg-primary-border',
    innerWrapperStyles:
      'bg-primary-btn shadow-primary-btn text-[#6C3C09] hover:bg-primary-btn-hover active:shadow-primary-btn-active',
    innerLoadingStyles: 'bg-primary-btn shadow-primary-btn text-[#6C3C09]',
  },
  return: {
    buttonStyles: 'bg-return-border',
    innerWrapperStyles:
      'bg-return-btn shadow-return-btn text-[#182948] active:shadow-return-btn-active',
    innerLoadingStyles: 'bg-return-btn shadow-return-btn text-[#182948]',
  },
  accept: {
    buttonStyles: 'bg-accept-border',
    innerWrapperStyles:
      'bg-accept-btn shadow-accept-btn text-[#184839] active:shadow-accept-btn-active',
    innerLoadingStyles: 'bg-accept-btn shadow-accept-btn text-[#184839]',
  },
};

export const ButtonWithLoader = forwardRef<HTMLButtonElement, IPrimaryButtonProps>((props, ref) => {
  const [progress, setProgress] = useState(0);
  const [animating, setAnimating] = useState(false);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    className,
    innerClassName,
    text,
    svg,
    onClick,
    disabled,
    isLoading,
    isAnimated = false,
    buttonType = 'primary',
    isMobile = false,
    onEnd,
    ...rest
  } = props;
  const { t } = useTranslation();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (!disabled && !isLoading) {
        audioManager.playSound(AUDIO_TYPES.CLICK);
        onClick();
      }
    },
    [onClick, disabled, isLoading],
  );

  // Loading overlay logic (right -> left)
  // While loading: start fully yellow (0), sweep to full black (100), then reset to 0 and repeat
  // When loading stops: go to full black (100) and stop
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (isLoading) {
      setAnimating(true);
      setProgress(0); // start from full yellow each time loading begins
      if (interval) clearInterval(interval);
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            return 0;
          }
          return prev + 0.2; // sweep speed while loading
        });
      }, 20);
    } else {
      // Finish by covering with black (to 100%) then stop
      if (interval) clearInterval(interval);
      if (animating) {
        interval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 100) {
              if (interval) clearInterval(interval);
              setAnimating(false);
              onEnd();
              return 100;
            }
            return prev + 2.5; // faster finish to black
          });
        }, 16);
      }
    }

    return () => {
      if (interval) clearInterval(interval);
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <button
      ref={ref}
      className={`min-h-[50px] rounded-xl p-0.5 outline-none sm:text-[22px] ${className ? className : ''} ${
        disabled
          ? 'bg-[#322E66] text-[12px] sm:text-[18px]'
          : buttonOptions[buttonType].buttonStyles
      }`}
      onClick={handleClick}
      disabled={disabled}
      {...rest}
    >
      <div
        className={`group relative inset-0 z-10 flex h-full items-center justify-center overflow-hidden rounded-[10px] px-4 py-[11px] text-base font-semibold sm:py-3 sm:leading-6 ${
          disabled
            ? 'bg-[#100E34] text-[#322E66] shadow-primary-btn-disabled sm:text-[18px]'
            : `${buttonOptions[buttonType].innerWrapperStyles} transition-all duration-300 ease-in-out`
        } ${innerClassName ? innerClassName : ''}`}
      >
        <div className="absolute inset-0 overflow-hidden rounded-[10px]">
          <div
            className="absolute bottom-0 right-0 top-0 h-full bg-[linear-gradient(180deg,_#0A0A42_0%,_#020037_100%)]"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div
          className={`relative z-40 flex items-center justify-center gap-1 transition-all duration-300 ease-out xs:tracking-normal ${!disabled ? 'group-active:scale-90' : ''} ${!disabled && isMobile ? 'text-[14px]' : ''} `}
        >
          {!disabled && svg}
          <span>{t(text ?? '')}</span>
        </div>
      </div>
    </button>
  );
});
